import { EMPTY_URL } from "csi/constants";
import { PreferenceView } from "csi/prefs/PreferenceView";
import { submitWriter } from "csi/service/writerService";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { preventDefaultBehaviour } from "designSystem/utilities/prevent-default-behavior";
import { uniqueId } from "lodash";
import { action, makeObservable, observable, runInAction } from "mobx";
import { cnl } from "src/c8-platform/type/value";
import CustomViewDialogStore from "../CustomViewDialogStore";
import { ViewCategory } from "./CustomViewDialogLeftPanelAccordion";
import { removeFavorite, saveFavorite } from "./customViewDialogLeftPanelService";

export class CustomViewItem {
	/** indicate whether or not to be able to delete this view */
	@observable canDelete: boolean = false;

	/** view category */
	readonly category: ViewCategory;

	/** description */
	@observable description: string;

	/** custom view dialog store */
	readonly #customViewDialogStore: CustomViewDialogStore;

	/** group of this custom view item belongs to */
	readonly group?: string;

	/** indicate if it's being deleted */
	@observable isDeleting: boolean = false;

	/** indicate if this item is favorite or not */
	@observable isFavorite?: boolean;

	/** indicate if partial editing is in progress */
	@observable isEditingPartial: boolean = false;

	/** name of the custom view */
	@observable name: string;

	/** original description */
	readonly originalDescription: string;

	/** original name */
	readonly originalName: string;

	/** indicate this item is selected */
	@observable selected?: boolean;

	/** url of the custom view */
	@observable url: cnl | null;

	/** unique id of custom view */
	id: string;

	/** indicate if enable the Save button or not */
	get enableSaveButton(): boolean {
		const { selected, isOOTBItem } = this;
		const { customViewStore: { showSave }, viewIsDirty, settingStore: { nameErrorRes } } = this.#customViewDialogStore;

		return showSave && selected && viewIsDirty && !isOOTBItem && !nameErrorRes;
	}

	/** indicate if the delete button should be disabled */
	get deleteButtonDisabled(): boolean {
		return !this.canDelete;
	}

	/** indicate if hide the Delete button or not */
	get hideDeleteButton(): boolean {
		const { category, deleteButtonDisabled, isDuplicated } = this;
		return (deleteButtonDisabled && category === ViewCategory.PUBLIC) || isDuplicated;
	}

	/** indicate if this item is duplicated */
	get isDuplicated(): boolean {
		return this.url === null;
	}

	/** indicate if this item is OOTB custom view */
	get isOOTBItem(): boolean {
		return this.url === EMPTY_URL;
	}

	/** indicate if this item is a personal view */
	get isPersonalView(): boolean {
		return this.category === ViewCategory.PERSONAL;
	}

	/** indicate if it's saving/removing favorite items  */
	get isProcessingFavorite(): boolean {
		return this.#customViewDialogStore.customViewStore.isLoadingFavorites;
	}

	/** indicate if this item is either duplicated or OOTB custom view */
	get isSpecialItem(): boolean {
		const { isDuplicated, isOOTBItem } = this;
		return isDuplicated || isOOTBItem;
	}

	/** indicate if show the Add to Favorite button or not */
	get showAddToFavorite(): boolean {
		const { isFavorite, isSpecialItem } = this;
		return !isFavorite && !isSpecialItem;
	}

	/** indicate if show the Discard button or not */
	get showDiscard(): boolean {
		return this.url === null;
	}

	/** indicate if show the Duplicate button or not */
	get showDuplicate(): boolean {
		const { selected, isOOTBItem } = this;
		return (!isOOTBItem && !selected) || (isOOTBItem && !this.#customViewDialogStore.viewIsDirty);
	}

	/** indicate if show the Remove from Favorite button or not */
	get showRemoveFromFavorite(): boolean {
		const { isFavorite, isSpecialItem } = this;
		return isFavorite && !isSpecialItem;
	}

	/** indicate if show the SaveAs button or not */
	get showSaveAs(): boolean {
		const { selected, isDuplicated, isOOTBItem } = this;
		return (!isOOTBItem && selected && !isDuplicated) ||
			(isOOTBItem && selected && this.#customViewDialogStore.viewIsDirty);
	}

	/** add the custom view to favorite list */
	addToFavorite = async (): Promise<void> => {
		const { customViewContainerUrl, customViewStore: { addFavoriteCustomViewItem, setIsLoadingFavorites }, setError } = this.#customViewDialogStore;
		const { url } = this;
		setIsLoadingFavorites(true);

		try {
			await saveFavorite(customViewContainerUrl, url);

			runInAction(() => {
				this.isFavorite = true;
				addFavoriteCustomViewItem(this);
			});
		} catch (ex) {
			setError(ex);
		}

		setIsLoadingFavorites(false);
	};

	/** cancel deleting the custom view */
	@action cancelDeleting = (event: React.MouseEvent<HTMLButtonElement>): void => {
		preventDefaultBehaviour(event);
		this.isDeleting = false;
	};

	/** proceed deleting the custom view */
	continueDeleting = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
		preventDefaultBehaviour(event);
		const { url } = this;
		const {
			cache,
			defaults,
			customViewStore,
			selectedCustomViewCnl,
		} = this.#customViewDialogStore;

		try {
			const writer = new Writer();
			writer.deleteNode(url);
			const response = await submitWriter(writer);

			runInAction(() => {
				cache.cacheResponse(response);
				defaults.remove(url);
				customViewStore.deleteCustomViewItem(this);

				// the view is deleted, the defaults info needs to be updated
				this.#customViewDialogStore.updateDefaults();

				if (selectedCustomViewCnl === url) {
					// setup the next selected view
					const { selectCustomView } = this.#customViewDialogStore;
					const { defaultCustomView } = customViewStore;
					let targetCustomView = defaultCustomView;

					if (!targetCustomView) {
						//  if we don't have set up any default view yet, use the default one
						targetCustomView = this.#customViewDialogStore.customViewStore.addOOTBCustomView();
					}

					selectCustomView(targetCustomView, true);
				}
			});
		} catch (ex) {
			runInAction(() => {
				this.#customViewDialogStore.failDeletingError = ex.toString();
			});
		} finally {
			runInAction(() => {
				this.isDeleting = false;
			});
		}
	};

	/** discard the duplicated view */
	@action discard = (): void => {
		const { customViewStore: { cancelDuplicateCustomView, defaultCustomView }, selectCustomView } = this.#customViewDialogStore;
		cancelDuplicateCustomView();

		// navigate to the default view
		selectCustomView(defaultCustomView, true);
	};

	/** duplicate the specified view */
	@action duplicate = (): void => {
		const { continueDuplicating } = this;
		const { customViewStore } = this.#customViewDialogStore;

		if (!!customViewStore.duplicatedCustomViewItem) {
			this.#customViewDialogStore.confirmDuplicationItem = this;
		} else {
			continueDuplicating(false);
		}
	};

	/** continue duplicating */
	continueDuplicating = (ignoreDirty: boolean): void => {
		const { customViewStore, selectCustomView } = this.#customViewDialogStore;
		const duplicatedItem = customViewStore.duplicateCustomViewItem(this, false, false);

		selectCustomView(duplicatedItem, ignoreDirty);
	};

	/** favorite button on click handler */
	onFavoriteButtonClick = (event: React.MouseEvent<HTMLElement>): void => {
		if (this.showAddToFavorite) {
			this.addToFavorite();
		} else if (this.showRemoveFromFavorite) {
			this.removeFromFavorites();
		}

		preventDefaultBehaviour(event);
	};

	/** callback when menu is going to open */
	onOpenMenu = async (event: React.MouseEvent<HTMLElement>): Promise<void> => {
		const { url } = this;
		const { customViewStore, getValidatedPreferenceView } = this.#customViewDialogStore;
		let cv: PreferenceView;

		preventDefaultBehaviour(event);

		if (url) {
			cv = await getValidatedPreferenceView(url);
		} else {
			// in the case of duplicate view, since they're not in database yet, fetch it from memory
			cv = customViewStore.duplicatedPreferenceView;
		}

		runInAction(() => {
			this.canDelete = cv.canDelete();
		});
	};

	/** save the partial custom view */
	onSavePartial = async (): Promise<boolean> => {
		const { description, name, originalDescription, originalName, stopEditingPartial, url } = this;
		const customViewDialogStore = this.#customViewDialogStore;
		const { setError, settingStore: { nameErrorRes } } = customViewDialogStore;
		if (nameErrorRes) {
			setError(new Error(nameErrorRes));
			return;
		}
		stopEditingPartial();
		const isDescriptionChanged = description !== originalDescription;
		const isNameChanged = name !== originalName;
		if (isDescriptionChanged || isNameChanged) {
			const writer = new Writer();
			const changeNode = writer.changeNode(url);

			if (isDescriptionChanged) {
				changeNode.changeAttribute("Description", xml.ATTR_STRING, description);
			}

			if (isNameChanged) {
				changeNode.changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, name);
			}

			try {
				await submitWriter(writer);
			} catch (error) {
				setError(new Error("RequestError"));
				return;
			}

			await this.#customViewDialogStore.refreshCustomViewStore(true);
		}
	};

	/** remove the custom view from the favorite list */
	@action removeFromFavorites = async (): Promise<void> => {
		const { customViewContainerUrl, setError, customViewStore: { removeFavoriteCustomViewItem, setIsLoadingFavorites } } = this.#customViewDialogStore;
		const { url } = this;
		setIsLoadingFavorites(true);

		try {
			await removeFavorite(customViewContainerUrl, url);

			runInAction(() => {
				this.isFavorite = false;
				removeFavoriteCustomViewItem(this);
			});
		} catch (ex) {
			setError(ex);
		}

		setIsLoadingFavorites(false);
	};

	/** set the description of the custom view from input element */
	@action setDescription = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const { value } = event.target;
		this.description = value;
	};

	/** set the name of the custom view from input element */
	@action setName = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const { value } = event.target;
		this.name = value;
	};

	/** save the current modified view, discard the changes on the duplicated view */
	saveAs = (): void => {
		const { customViewStore, selectCustomView } = this.#customViewDialogStore;
		const duplicatedItem = customViewStore.duplicateCustomViewItem(this, true, false);

		selectCustomView(duplicatedItem, true);
	};

	/** save the current view */
	save = async (): Promise<C8Node | undefined> => {
		return await this.#customViewDialogStore.saveCustomView(this.url, false);
	};

	/** search view by name */
	searchViewByName = (text: string): boolean => {
		const trimmedText = text.trim();

		if (!trimmedText) {
			return true;
		}

		return this.name.toLocaleLowerCase().includes(trimmedText.toLocaleLowerCase());
	};

	/** select the current view */
	select = (): void => {
		const { url } = this;
		const {
			customViewStore: { selectedCustomViewItem },
			selectCustomView,
			selectedCustomViewCnl,
			selectedViewIsOOTB,
		} = this.#customViewDialogStore;

		const ignoreDirty = selectedViewIsOOTB || !selectedCustomViewItem?.enableSaveButton;

		if (url !== selectedCustomViewCnl) {
			selectCustomView(this, ignoreDirty);
		}
	};

	/** pop up the confirmation dialog to delete the custom view */
	@action startDeleting = (): void => {
		this.isDeleting = true;
	};

	/** cancel editing partial view */
	@action cancelEditingPartial = (): void => {
		this.isEditingPartial = false;
		this.name = this.originalName;
		this.description = this.originalDescription;
	};

	/** set isEditingPartial to true */
	@action startEditingPartial = (): void => {
		this.isEditingPartial = true;
	};

	/** set isEditingPartial to false */
	@action stopEditingPartial = (): void => {
		this.isEditingPartial = false;
	};

	constructor(
		url: cnl,
		name: string,
		category: ViewCategory,
		customViewDialogStore?: CustomViewDialogStore,
		group?: string,
		selected?: boolean,
		isFavorite?: boolean,
		description?: string,
	) {
		makeObservable(this);
		this.url = url;
		this.id = url || uniqueId();
		this.name = this.originalName = name;
		this.group = group;
		this.category = category;
		this.#customViewDialogStore = customViewDialogStore;
		this.isFavorite = isFavorite;
		this.selected = selected;
		this.description = this.originalDescription = description;
	}
}